import React, { memo, useMemo } from 'react';
import { Proposal } from '../types';
import { BlockContainer } from '../BlockContainer';
import './index.scss';
import { formGroup, formControl, FormControl, FormDataType, formArray } from '@core/utils/form';
import { defineBlockAdvanceForm } from '../Common/AdvanceSettings';
import { useNonNilObservable, useObservable } from '@core/utils/hooks/rxjs';
import { useConfigureBlock } from '@modules/proposal/hooks';
import { useProposalContext, useProposalDataContext } from '@modules/proposal/ProposalBuilderPage';
import { HTMLService, ProposalService } from '@business/services';
import { FormHTMLInline, FormInput } from '@core/components/form';
import { openGenericRTEModal } from '@modules/common/GenericRTEModal';
import { TableColumn } from '@modules/common/TableColumn';
import { TableActions } from '@modules/common/TableActions';
import { Button } from '@core/components/Button';
import { Formatter } from '@core/utils/formatter';
import { R } from '@core/utils/r';
import { CategoryId } from '@business/entities/category';
import { ProjectListBlockSettings } from './ProjectListBlockSettings';
import { ProjectListNodeType } from './types';

export { ProjectListNodeType };

function defineProjectListVendorForm() {
  return formGroup({
    name: formControl<string>({ initialValue: '' }),
    subcontractorName: formControl<string>({ initialValue: '' }),
    phone: formControl<string>({ initialValue: '' }),
    email: formControl<string>({ initialValue: '' }),
  });
}

function defineProjectListVccForm() {
  return formGroup({
    description: formControl<string>({ initialValue: '' }),
    // Where the row comes from, shown under the description: "Option: <title>" or "List item" (the estimate's own rows
    // and rows added by hand have none)
    note: formControl<string>({ initialValue: '' }),
    quantity: formControl<number>({ initialValue: 0 }),
    price: formControl<number>({ initialValue: 0 }),
  });
}

// A node is one piece of a category's content. `text`/`calendarEvent` are inline-editable text;
// `vendors`/`vccItems` hold the work order's real values once baked (empty = template placeholders).
export function defineProjectListNodeForm() {
  return formGroup({
    type: formControl<ProjectListNodeType>({ initialValue: ProjectListNodeType.text }),
    content: formControl<string>({ initialValue: '' }),
    vendorLimit: formControl<number>({ initialValue: 3 }),
    vendorFields: formControl<string[]>({ initialValue: ['name'] }),
    vendorsLayout: formControl<'vertical' | 'horizontal'>({ initialValue: 'vertical' }),
    vendors: formArray(() => defineProjectListVendorForm()),
    vccItems: formArray(() => defineProjectListVccForm()),
  });
}

export function defineProjectListCategoryForm() {
  return formGroup({
    categoryId: formControl<CategoryId>({}),
    // The category's name, saved when the list is filled (pages that can't load the categories, e.g. the client's
    // proposal link, show this one)
    title: formControl<string>({ initialValue: '' }),
    nodes: formArray(() => defineProjectListNodeForm()),
  });
}

// The default titles of the options' and list items' VCC sections (ProjectListHelper has the same)
export const PROJECT_LIST_OPTION_ITEMS_TITLE = 'Options';
export const PROJECT_LIST_LIST_ITEMS_TITLE = 'List Items';

export function defineProjectListBlockForm() {
  return formGroup({
    // The headings (HTML) are edited on the block itself (edit icon next to each, while editing)
    heading: formControl<string>({ label: 'Heading', initialValue: 'Project List' }),
    // General settings (all categories): also show these VCC items when the list is filled (a proposal: the options and
    // list items attached to it; a work order's list: all of its own, accepted or not), each in its own section after the
    // categories, under its title
    showOptionItems: formControl<boolean>({ label: 'Show VCC items of options', initialValue: false }),
    optionItemsTitle: formControl<string>({ label: 'Options section title', initialValue: PROJECT_LIST_OPTION_ITEMS_TITLE }),
    showListItems: formControl<boolean>({ label: 'Show VCC items of list items', initialValue: false }),
    listItemsTitle: formControl<string>({ label: 'List items section title', initialValue: PROJECT_LIST_LIST_ITEMS_TITLE }),
    categories: formArray(() => defineProjectListCategoryForm(), { initialValue: [undefined] }),
    // Filled with the list: the options' and list items' VCC, one category (title and a VCC table) per category
    optionCategories: formArray(() => defineProjectListCategoryForm()),
    listCategories: formArray(() => defineProjectListCategoryForm()),
    advance: defineBlockAdvanceForm(),
  });
}

export type ProjectListBlockForm = ReturnType<typeof defineProjectListBlockForm>;
export type ProjectListBlockFormData = FormDataType<typeof defineProjectListBlockForm>;
export type ProjectListCategoryForm = ReturnType<typeof defineProjectListCategoryForm>;
export type ProjectListCategoryFormData = FormDataType<typeof defineProjectListCategoryForm>;
export type ProjectListNodeForm = ReturnType<typeof defineProjectListNodeForm>;
export type ProjectListNodeFormData = FormDataType<typeof defineProjectListNodeForm>;
export type ProjectListVendorForm = ReturnType<typeof defineProjectListVendorForm>;

export enum ProjectListBlockVariation {
  list = 'list',
}

// Placeholder tokens shown in the template builder (before real values are baked in).
const VENDOR_TOKENS: Record<string, string> = {
  name: '{{vendorName}}',
  subcontractor: '{{subcontractorName}}',
  phone: '{{phone}}',
  email: '{{email}}',
};

const vendorFieldControl = (vendor: ProjectListVendorForm, field: string): FormControl<string> | null => {
  switch (field) {
    case 'name':
      return vendor.controls.name;
    case 'subcontractor':
      return vendor.controls.subcontractorName;
    case 'phone':
      return vendor.controls.phone;
    case 'email':
      return vendor.controls.email;
    default:
      return null;
  }
};

export const ProjectListBlock = memo((props: Proposal.BlockProps<ProjectListBlockVariation>) => {
  const { editable } = props;
  const form = useMemo(defineProjectListBlockForm, []);
  const formValue = useNonNilObservable(form.value$);
  const data = useProposalDataContext();
  const transformed = useMemo(() => ProposalService.getTransformedValues(data), [data]);

  const proposal = useProposalContext();
  const bloc = proposal?.bloc;
  const categories = useObservable(bloc?.categories$, []) ?? [];
  const categoryControls = useNonNilObservable(form.controls.categories.items$);
  const optionCategoryControls = useNonNilObservable(form.controls.optionCategories.items$);
  const listCategoryControls = useNonNilObservable(form.controls.listCategories.items$);

  // The category's current name when the categories are loaded (template builder, work order), else the saved one
  const categoryTitle = (category: ProjectListCategoryFormData) =>
    categories.find(c => c.id === category.categoryId)?.title || category.title || 'Category';

  useConfigureBlock({
    props,
    form,
    contentProps: { form, categories },
    contentComponent: ProjectListBlockSettings,
    onlyAdvance: true,
    modelWidth: 'lg',
  });

  const renderVendorCard = (fields: string[], key: number, vendor?: ProjectListVendorForm) => (
    <div className="pl-vendor-card" key={key}>
      {fields.map(f => {
        const control = vendor ? vendorFieldControl(vendor, f) : null;
        return (
          <div key={f} className="pl-vendor-line">
            {control ? editable ? <FormInput control={control} /> : <span>{control.value}</span> : <span className="pl-token">{VENDOR_TOKENS[f] ?? `{{${f}}}`}</span>}
          </div>
        );
      })}
    </div>
  );

  const renderNode = (nodeCtrl: ProjectListNodeForm, key: number) => {
    const node = nodeCtrl.value;
    switch (node.type) {
      case ProjectListNodeType.text:
      case ProjectListNodeType.calendarEvent:
        return (
          <div key={key} className="pl-node pl-node-text">
            <FormHTMLInline control={nodeCtrl.controls.content} multipleLines readonly={!editable} transformedFieldsValue={transformed} />
          </div>
        );
      case ProjectListNodeType.vendors: {
        const fields = node.vendorFields?.length ? node.vendorFields : ['name'];
        const layout = node.vendorsLayout ?? 'vertical';
        const vendorCtrls = nodeCtrl.controls.vendors.items;
        return (
          <div key={key} className={`pl-node pl-vendors pl-vendors-${layout}`}>
            {vendorCtrls.length ? vendorCtrls.map((v, i) => renderVendorCard(fields, i, v)) : [0, 1].map(i => renderVendorCard(fields, i))}
          </div>
        );
      }
      case ProjectListNodeType.vccItems: {
        const vccCtrls = nodeCtrl.controls.vccItems.items;
        // An empty VCC table only shows while editing (to add rows). Otherwise nothing shows, so a work order's list
        // and the client portal never show the template's {{description}} / {{quantity}} / {{price}} tokens.
        if (!vccCtrls.length && !editable) {
          return null;
        }
        return (
          <div key={key} className="pl-node pl-vcc">
            <div className="pl-vcc-row pl-vcc-head">
              <TableColumn flex={3}>Description</TableColumn>
              <TableColumn flex={1}>Quantity</TableColumn>
              <TableColumn flex={1}>Price</TableColumn>
              {editable && <TableColumn flex={0.3} />}
            </div>
            {vccCtrls.map((item, i) => (
              <div key={i} className="pl-vcc-row">
                <TableColumn flex={3}>
                  {editable ? <FormInput control={item.controls.description} /> : item.value.description}
                  {!!item.value.note && <div className="text-muted font-size-12">{item.value.note}</div>}
                </TableColumn>
                <TableColumn flex={1}>{editable ? <FormInput control={item.controls.quantity} type="number" /> : item.value.quantity}</TableColumn>
                <TableColumn flex={1}>
                  {editable ? <FormInput control={item.controls.price} type="number" /> : Formatter.formatCurrency(item.value.price, 'USD')}
                </TableColumn>
                {editable && (
                  <TableColumn flex={0.3}>
                    <TableActions onDelete={() => nodeCtrl.controls.vccItems.removeAt(i)} />
                  </TableColumn>
                )}
              </div>
            ))}
            {editable && (
              <Button color="primary" outline size="small" extraClassNames="mt-2" onClick={() => nodeCtrl.controls.vccItems.push()}>
                + Add Row
              </Button>
            )}
          </div>
        );
      }
      default:
        return null;
    }
  };

  const renderCategory = (categoryCtrl: ProjectListCategoryForm, index: number) => (
    <div key={index} className="project-list-category">
      <h3 className="category-title">{categoryTitle(categoryCtrl.value)}</h3>
      {categoryCtrl.controls.nodes.items.map((nodeCtrl, nodeIndex) => renderNode(nodeCtrl, nodeIndex))}
    </div>
  );

  // The block's heading and the two section titles (HTML). While editing, the edit icon next to one opens the rich-text
  // editor in a modal, so the settings window stays short.
  const renderHeading = (control: FormControl<string>) => {
    if (!editable) {
      return !!control.value && <div className="project-list-heading">{HTMLService.toReact(control.value)}</div>;
    }
    const label = control.config.label ?? '';
    const onEdit = () => openGenericRTEModal({ label, initialValue: control.value, onSubmit: value => control.setValue(value) });
    return (
      <div className="project-list-heading d-flex align-items-start">
        <div className="flex-grow-1">{control.value ? HTMLService.toReact(control.value) : <span className="text-muted">{label}</span>}</div>
        <TableActions onEdit={onEdit} />
      </div>
    );
  };

  // The options' or list items' VCC: their own section after the categories, under its title, one VCC table per
  // category. While editing with the setting on, an empty one says what shows there (the template has no data).
  const renderVccSection = (titleControl: FormControl<string>, sectionCategories: ProjectListCategoryForm[], isOn: boolean, emptyText: string) => {
    if (!sectionCategories.length && !(editable && isOn)) {
      return null;
    }
    return (
      <div className="project-list-vcc-section">
        {renderHeading(titleControl)}
        {sectionCategories.length ? sectionCategories.map(renderCategory) : <div className="text-muted">{emptyText}</div>}
      </div>
    );
  };

  return (
    <BlockContainer
      className="block-container d-flex flex-fill block-text"
      advanceForm={formValue.advance}
      style={{ padding: props.preview ? '30px 20px' : '0px' }}
      isSection={props.isSection}
      sectionTitle={props.sectionTitle}
      fullWidth={false}
      containerStyles={R.objectCompact(
        R.pick(formValue.advance, ['borderRadius', 'borderWidth', 'borderStyle', 'paddingTop', 'paddingBottom', 'paddingLeft', 'paddingRight']),
      )}
      editable={editable}
    >
      <div className="project-list-block">
        {renderHeading(form.controls.heading)}
        {categoryControls.map(renderCategory)}
        {renderVccSection(
          form.controls.optionItemsTitle,
          optionCategoryControls,
          formValue.showOptionItems,
          'The VCC items of the options show here, one table per category.',
        )}
        {renderVccSection(
          form.controls.listItemsTitle,
          listCategoryControls,
          formValue.showListItems,
          'The VCC items of the list items show here, one table per category.',
        )}
      </div>
    </BlockContainer>
  );
});
